import { useState, useEffect, useCallback, useRef } from 'react';
import { adminManagementService } from '@/admin/services/adminManagement';
import {
  SchoolDetailsResponse,
  SchoolDetailsError,
} from '@/school-management/types/services/adminManagement';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

type ErrorType = 'network' | 'validation' | 'not_found' | 'server' | 'unauthorized' | 'unknown';

interface ErrorState {
  type: ErrorType;
  message: string;
  code?: string;
  statusCode?: number;
  canRetry: boolean;
  retryAfter?: number; // seconds
}

interface UseSchoolDetailsState {
  data: SchoolDetailsResponse | null;
  isLoading: boolean;
  error: ErrorState | null;
  retryCount: number;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

type BackgroundFetchSubscriber = (response: SchoolDetailsResponse | null) => void;

type InflightBackgroundFetch = {
  promise: Promise<SchoolDetailsResponse | null>;
  subscribers: Set<BackgroundFetchSubscriber>;
  settled: boolean;
  result: SchoolDetailsResponse | null;
};

/** Coalesce concurrent background fetches for the same org across hook instances. */
const inflightBackgroundFetches = new Map<string, InflightBackgroundFetch>();

const notifySubscribers = (
  entry: InflightBackgroundFetch,
  response: SchoolDetailsResponse | null
) => {
  entry.result = response;
  entry.settled = true;
  [...entry.subscribers].forEach(subscriber => subscriber(response));
  entry.subscribers.clear();
};

const runSharedBackgroundFetch = (
  orgId: string,
  onResult: BackgroundFetchSubscriber
): Promise<SchoolDetailsResponse | null> => {
  const existing = inflightBackgroundFetches.get(orgId);
  if (existing) {
    if (existing.settled) {
      onResult(existing.result);
    } else {
      existing.subscribers.add(onResult);
    }
    return existing.promise;
  }

  const entry: InflightBackgroundFetch = {
    promise: Promise.resolve(null),
    subscribers: new Set<BackgroundFetchSubscriber>([onResult]),
    settled: false,
    result: null,
  };

  entry.promise = adminManagementService
    .getSchoolDetails(orgId)
    .then(response => {
      notifySubscribers(entry, response);
      return response;
    })
    .catch(() => {
      notifySubscribers(entry, null);
      return null;
    })
    .finally(() => {
      inflightBackgroundFetches.delete(orgId);
    });

  inflightBackgroundFetches.set(orgId, entry);
  return entry.promise;
};

const getErrorType = (error: SchoolDetailsError): ErrorType => {
  const { statusCode, code } = error;
  const sc = typeof statusCode === 'number' ? statusCode : -1;

  if (sc === 401 || sc === 403) return 'unauthorized';
  if (sc === 404) return 'not_found';
  if (sc >= 400 && sc < 500) return 'validation';
  if (sc >= 500) return 'server';
  if (code?.includes('NETWORK') || statusCode === undefined) return 'network';

  return 'unknown';
};

const createErrorState = (error: SchoolDetailsError): ErrorState => {
  const type = getErrorType(error);
  const canRetry =
    type === 'network' ||
    type === 'server' ||
    (error.statusCode !== undefined && error.statusCode >= 500);

  let message = error.message;

  // User-friendly error messages
  switch (type) {
    case 'not_found':
      message = `School with ID '${error.schoolId}' was not found`;
      break;
    case 'unauthorized':
      message = "You are not authorized to view this school's details";
      break;
    case 'network':
      message = 'Network connection error. Please check your internet connection';
      break;
    case 'server':
      message = 'Server is temporarily unavailable. Please try again later';
      break;
    case 'validation':
      message = error.message || 'Invalid school ID provided';
      break;
  }

  return {
    type,
    message,
    code: error.code,
    statusCode: error.statusCode,
    canRetry,
    retryAfter: type === 'server' ? 5 : 2,
  };
};

export const useSchoolDetails = (schoolId?: string) => {
  const [state, setState] = useState<UseSchoolDetailsState>({
    data: null,
    isLoading: false,
    error: null,
    retryCount: 0,
  });

  const { userRole, user } = useAppSelector(authSelector);

  const abortControllerRef = useRef<AbortController | null>(null);
  const retryTimeoutRef = useRef<any>(null);
  const backgroundFetchIdRef = useRef(0);

  const fetchSchoolDetails = useCallback(async (id: string, isRetry: boolean = false) => {
    if (!id || id.trim() === '') {
      setState(prev => ({
        ...prev,
        error: {
          type: 'validation',
          message: 'School ID is required and cannot be empty',
          canRetry: false,
        },
        isLoading: false,
      }));
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      retryCount: isRetry ? prev.retryCount + 1 : 0,
    }));

    try {
      const response = await adminManagementService.getSchoolDetails(
        user?.metadata?.organizationDetails?.organizationId || ''
      );
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (response === null) {
        // Handle null response as an error
        const errorState: ErrorState = {
          type: 'server',
          message: 'Failed to fetch school details',
          canRetry: true,
          retryAfter: 2,
        };

        setState(prev => ({
          ...prev,
          data: null,
          isLoading: false,
          error: errorState,
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        data: response,
        isLoading: false,
        error: null,
        retryCount: 0,
      }));
    } catch (error: any) {
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      const errorState = createErrorState(error as SchoolDetailsError);

      setState(prev => ({
        ...prev,
        data: null,
        isLoading: false,
        error: errorState,
      }));
    }
  }, []);

  const retry = useCallback(() => {
    if (!schoolId || !state.error?.canRetry || state.retryCount >= MAX_RETRY_ATTEMPTS) {
      return;
    }

    const delay = state.error.retryAfter
      ? state.error.retryAfter * 1000
      : RETRY_DELAY * Math.pow(2, state.retryCount);

    retryTimeoutRef.current = setTimeout(() => {
      fetchSchoolDetails(schoolId, true);
    }, delay);
  }, [schoolId, state.error, state.retryCount, fetchSchoolDetails]);

  const refetch = useCallback(() => {
    if (schoolId) {
      setState(prev => ({ ...prev, retryCount: 0 }));
      fetchSchoolDetails(schoolId, false);
    }
  }, [schoolId, fetchSchoolDetails]);

  /** Updates school details payload without toggling isLoading (avoids full-page loader). */
  const refetchInBackground = useCallback(() => {
    const orgId = user?.metadata?.organizationDetails?.organizationId || '';
    if (!schoolId || !orgId) return;
    const requestId = ++backgroundFetchIdRef.current;
    void runSharedBackgroundFetch(orgId, response => {
      if (requestId !== backgroundFetchIdRef.current) return;
      if (response === null) return;
      setState(prev => ({
        ...prev,
        data: response,
      }));
    });
  }, [schoolId, user?.metadata?.organizationDetails?.organizationId]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  useEffect(() => {
    if (schoolId) {
      fetchSchoolDetails(schoolId, false);
    } else {
      setState({
        data: null,
        isLoading: false,
        error: null,
        retryCount: 0,
      });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [schoolId, fetchSchoolDetails]);

  return {
    ...state,
    refetch,
    refetchInBackground,
    retry,
    clearError,
    fetchSchoolDetails,
    canRetry: state.error?.canRetry && state.retryCount < MAX_RETRY_ATTEMPTS,
    isRetrying: state.retryCount > 0 && state.isLoading,
  };
};
